import { cn } from "@/lib/utils";

export type MetricKey =
  | "opening"
  | "txIn"
  | "txOut"
  | "fees"
  | "expected"
  | "ledgerExpected"
  | "gap"
  | "exp";

export type MetricCell = {
  val: number | null;
  ccy: any;
  title?: string;
  className?: string;
  subText?: string;
};

export type MetricMaps = {
  opening?: Map<string, any>;
  expected?: Map<string, any>;
  ledgerExpected?: Map<string, any>;
  gap?: Map<string, any>;
  txInOut?: Map<string, any>;
  fees?: Map<string, any>;
  paidExpenses?: Map<string, any>;
};

export type MetricMeta = {
  key: MetricKey;
  label: string;
  pill: string;
  rowClass: string;
  leftBg: string;
  getCell: (args: {
    dateKey: string;
    rowCcyFallback: any;
    maps: MetricMaps;
  }) => MetricCell;
};

export const GAP_EPS = 0.01;

export const buildReconLink = (args: {
  accountId: string;
  date: string; // YYYY-MM-DD
  onlyGaps?: boolean;
}) => {
  const sp = new URLSearchParams();
  sp.set("accountId", args.accountId);
  sp.set("dateFrom", args.date);
  sp.set("dateTo", args.date);
  if (args.onlyGaps) sp.set("onlyGaps", "1");
  return `/finance/banking/reconciliation?${sp.toString()}`;
};

export const gapBg = (v: number | null) => {
  if (v == null) return "";
  if (Math.abs(v) <= GAP_EPS) return "bg-emerald-200/60";
  if (v < -GAP_EPS) return "bg-rose-200/60";
  return "bg-rose-100/60";
};

export const gapRing = (v: number | null) => {
  if (v == null) return "";
  if (Math.abs(v) <= GAP_EPS) return "ring-1 ring-emerald-200/70";
  if (v < -GAP_EPS) return "ring-1 ring-rose-200/70";
  return "ring-1 ring-rose-200/40";
};

export function prettyDate(dateKey: string) {
  // dateKey: "YYYY-MM-DD"
  // Важно: парсим как UTC, чтобы не было сдвигов дня из-за таймзоны.
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));

  const parts = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(dt);

  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const year = parts.find((p) => p.type === "year")?.value ?? "";

  return `${day} ${month}, ${year}`;
}

export const METRICS: MetricMeta[] = [
  {
    key: "opening",
    label: "Opening",
    pill: "Opening",
    rowClass: "bg-white hover:bg-slate-50/50",
    leftBg: "bg-white",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const cell = maps.opening?.get(dateKey);
      const val = (cell?.opening_balance ?? null) as number | null;
      const ccy = (cell?.currency ?? rowCcyFallback) as any;
      return {
        val,
        ccy,
        title: cell?.opening_balance_ts ?? "",
        className: val == null ? "text-slate-300" : "text-slate-900 font-medium",
      };
    },
  },
  {
    key: "txIn",
    label: "Tx In (credit)",
    pill: "IN",
    rowClass: "bg-white",
    leftBg: "bg-white",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const r = maps.txInOut?.get(dateKey);
      const ccy = r?.currency ?? rowCcyFallback;

      const amount = r?.in_amount ?? 0;
      const count = r?.in_count ?? 0;

      return {
        val: r ? amount : null,
        ccy,
        subText: r ? `${count} transactions` : undefined,
      };
    },
  },
  {
    key: "txOut",
    label: "Tx Out (debit)",
    pill: "OUT",
    rowClass: "bg-white",
    leftBg: "bg-white",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const r = maps.txInOut?.get(dateKey);
      const ccy = r?.currency ?? rowCcyFallback;

      const amount = r?.out_amount ?? 0;
      const count = r?.out_count ?? 0;

      return {
        val: r ? amount : null,
        ccy,
        subText: r ? `${count} transactions` : undefined,
      };
    },
  },
  {
    key: "fees",
    label: "Fees (sum)",
    pill: "FEE",
    rowClass: "bg-white",
    leftBg: "bg-white",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const r = maps.fees?.get(dateKey);
      const ccy = r?.currency ?? rowCcyFallback;

      const total = r?.fee_total ?? 0;
      const count = r?.tx_count ?? 0;

      return {
        val: r ? total : null,
        ccy,
        subText: r ? `${count} transactions` : undefined,
        className: r ? "text-slate-700 font-semibold" : "text-slate-300",
      };
    },
  },
  {
    key: "expected",
    label: "Expected closing",
    pill: "Expected",
    rowClass: "bg-slate-50/40 hover:bg-slate-50/70",
    leftBg: "bg-slate-50/40",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const cell = maps.expected?.get(dateKey);
      const val = (cell?.expected_closing ?? null) as number | null;
      const ccy = (cell?.currency ?? rowCcyFallback) as any;
      return {
        val,
        ccy,
        className: val == null ? "text-slate-300" : "text-slate-700 font-semibold",
      };
    },
  },
  {
    key: "exp",
    label: "Expenses (paid)",
    pill: "EXPENSES",
    rowClass: "bg-white",
    leftBg: "bg-white",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const r = maps.paidExpenses?.get(dateKey);
      const ccy = r?.currency ?? rowCcyFallback;

      const total = r?.paid_total ?? 0;
      const count = r?.paid_count ?? 0;

      return {
        val: r ? total : null,
        ccy,
        subText: r ? `${count} paid expenses` : undefined,
      };
    },
  },
  {
    key: "ledgerExpected",
    label: "Ledger expected",
    pill: "Ledger",
    rowClass: "bg-white hover:bg-slate-50/50",
    leftBg: "bg-white",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const cell = maps.ledgerExpected?.get(dateKey);
      const val = (cell?.ledger_expected_closing ?? null) as number | null;
      const ccy = (cell?.currency ?? rowCcyFallback) as any;
      const title = cell
        ? `opening=${cell.opening_balance ?? "—"} credit=${cell.credit_in} paidExp=${cell.paid_expense_total} fee=${cell.fee_total}`
        : "";
      return {
        val,
        ccy,
        title,
        className: val == null ? "text-slate-300" : "text-slate-900 font-semibold",
      };
    },
  },
  {
    key: "gap",
    label: "Ledger gap",
    pill: "Gap",
    rowClass: "bg-rose-50/20 hover:bg-rose-50/40",
    leftBg: "bg-rose-50/20",
    getCell: ({ dateKey, rowCcyFallback, maps }) => {
      const cell = maps.gap?.get(dateKey);
      const val = (cell?.ledger_gap ?? null) as number | null;
      const ccy = (cell?.currency ?? rowCcyFallback) as any;
      const title = cell?.real_closing_ts ?? "";
      const className =
        val == null
          ? "text-slate-300"
          : Math.abs(val) > 0.01
            ? "text-rose-600"
            : "text-emerald-600";
      return { val, ccy, title, className: cn("font-bold", className) };
    },
  },
];